import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator/ClubStackNavigator";
import {
  Button,
  Radio,
  RadioGroup,
} from "@whoosh/design-system/src/components/controls";
import { Avatar } from "@whoosh/design-system/src/components/display";
import { LoadingSpinner } from "@whoosh/design-system/src/components/display/Loader/LoadingSpinner";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PanelHeader } from "@whoosh/design-system/src/components/layout/PanelHeader";
import {
  VStack,
  Text,
  HStack,
  Box,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";

import { goToCancelBookingStepAtom } from "../../atoms/actions/goToCancelBookingStepAtom";
import { getMemberRoundsAtom } from "../../atoms/selectors/getMemberRoundsAtom";
import { useRemoveCurrentPlayer } from "./hooks/useRemoveCurrentPlayer";
import { useTransferBookingOwnershipAndLeave } from "./hooks/useTransferBookingOwnership";

type Props = {
  currentUserClientId: string;
  onClose: () => void;
};

export const TransferOwnership = ({ currentUserClientId, onClose }: Props) => {
  const goToCancelBookingStep = useSetAtom(goToCancelBookingStepAtom);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | undefined>(
    undefined
  );
  const [transferAndLeave, isTransferingBooking] =
    useTransferBookingOwnershipAndLeave();

  const [removeCurrentPlayer, isRemovingCurrentPlayer] =
    useRemoveCurrentPlayer();

  const memberRounds = useAtomValue(getMemberRoundsAtom);

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList, "Home">>();

  const memberRoundsWithoutCurrenOwner = memberRounds?.filter(
    (r) => r.isOwner !== true
  );

  const transferAndLeaveHandler = () => {
    transferAndLeave(currentUserClientId, selectedPlayerId ?? "", () => {
      onClose();
      navigation.navigate("Home", { screen: "MainHome" });
    });
  };

  const removeCurrentPlayerHandler = () => {
    removeCurrentPlayer(currentUserClientId, () => {
      onClose();
      navigation.navigate("Home", { screen: "MainHome" });
    });
  };

  const isLoading = isRemovingCurrentPlayer || isTransferingBooking;

  return (
    <>
      {isLoading ? (
        <Box alignItems="center" justifyContent="center" flex={1}>
          <LoadingSpinner />
        </Box>
      ) : (
        <VStack flex={1} backgroundColor="surface-primary" paddingBottom={6}>
          <PanelHeader
            paddingVariant="medium"
            hideBottomBorder
            header=""
            outerLeftComponent={
              <Button
                testID="modal-close-btn"
                variant="secondary"
                accessibilityLabel={t`Close button`}
                onPress={goToCancelBookingStep}
                leftIcon={<ArrowLeftIcon />}
              />
            }
          />
          <VStack flex={1} backgroundColor="surface-primary" px={6}>
            <VStack gap={4} marginTop={1}>
              <Text variant={"mainTitle2"}>{t`Transfer Ownership`}</Text>
              <Text variant="mainBaseMedium" color="text-primary">
                {t`Before leaving, assign ownership to someone else.`}
              </Text>
            </VStack>

            <Box marginTop={10}>
              <RadioGroup
                onChange={(val) => setSelectedPlayerId(val)}
                value={selectedPlayerId}
              >
                {memberRoundsWithoutCurrenOwner?.map((round) => (
                  <Pressable
                    key={round.player?.id}
                    onPress={() =>
                      setSelectedPlayerId(round.player?.id ?? undefined)
                    }
                  >
                    <HStack
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <HStack gap={4} alignItems="center">
                        <Avatar
                          profileImagePublicId={
                            round.player?.profileImagePublicId ?? undefined
                          }
                          size="md"
                          firstName={round.player?.firstName ?? ""}
                          lastName={round.player?.lastName ?? ""}
                        />
                        <Text variant="mainBaseMedium">
                          {round.player?.firstName} {round.player?.lastName}
                        </Text>
                      </HStack>
                      <Radio
                        value={round.player?.id ?? ""}
                        testID={`player-radio-${round.player?.id}`}
                      />
                    </HStack>
                  </Pressable>
                ))}
              </RadioGroup>
            </Box>
          </VStack>

          {/* buttons */}
          <VStack gap={2} px={6}>
            <Button
              variant="secondary"
              onPress={removeCurrentPlayerHandler}
              accessibilityLabel={t`Remain Booking Owner and Leave button`}
            >{t`Remain Booking Owner and Leave`}</Button>
            <Button
              variant="main"
              isDisabled={!selectedPlayerId}
              onPress={transferAndLeaveHandler}
              accessibilityLabel={t`Transfer and Leave button`}
            >{t`Transfer and Leave`}</Button>
          </VStack>
        </VStack>
      )}
    </>
  );
};
